import { GetServerSidePropsContext } from 'next';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { TenantTypes } from '@tenants';
import Layout from 'components/layout';
import { ListingsSearchPage } from 'components/pages/exp-commercial/listings/search';
import { NOT_FOUND } from 'constants/not-found';
import { FeaturesType, ListingsFilterProvider } from 'contexts';
import { createGetServerSideProps, HandlerType } from 'utils/next/shared-getServerSideProps';
import { ThemeProvider } from '@mui/material/styles';
import { muiTheme } from 'tenants/styles/exp-commercial-mui';
import { getStates, getCities } from 'data/commercial/filters';
import logger from 'utils/logger';
import HeadData from 'components/head-data';
import { SEARCH_PAGE_ROUTE_NAME } from 'types/search_page_types';
import { ListingsFilterProviderProps } from 'contexts/exp-commercial/listings-filter';
import { getTenantShowcaseFeature } from '@tenants/utils/features';

type Props = Omit<ListingsFilterProviderProps, 'children'>;
// TODO: long term is to statically generate this page
// but is an issue because it defaults to zoocasa tenant
const cache = new Map<string, Props>();
const cacheTimeout = 1000 * 60 * 60 * 12; // 12 hours
setInterval(() => {
  cache.clear();
}, cacheTimeout);

const handler: HandlerType<Props> = async (_context: GetServerSidePropsContext, _features?: FeaturesType, tenant?: TenantTypes) => {
  const showcaseFeature = getTenantShowcaseFeature(tenant);
  if (!showcaseFeature.expCommercialEnabled) {
    // we should not show the listings page when eXp Commercial listings are not supported by the tenant
    return NOT_FOUND;
  }

  if (cache.has(tenant.id)) {
    return {
      props: cache.get(tenant.id),
    };
  }

  try {
    const abortController = new AbortController();
    const timeout = setTimeout(() => {
      abortController.abort();
    }, 10_000);

    const [stateResponse, citiesResponse] = await Promise.all([getStates(abortController), getCities(abortController)]);
    clearTimeout(timeout);
    abortController.abort();

    cache.set(tenant.id, {
      availableStates: stateResponse.states,
      availableCities: citiesResponse.cities,
    });

    return {
      props: {
        availableStates: stateResponse.states,
        availableCities: citiesResponse.cities,
      },
    };
  } catch (error) {
    logger.error('Error fetching states and cities:', error);
    return {
      props: {
        availableStates: [],
        availableCities: [],
      },
    };
  }
};

export const getServerSideProps = createGetServerSideProps<Props>(handler);

const Page = ({ availableStates, availableCities }: Props) => {
  return (
    <ThemeProvider theme={muiTheme}>
      <HeadData data={{ title: 'Listings - eXp Commercial' }} />
      <NuqsAdapter>
        <ListingsFilterProvider
          availableStates={availableStates}
          availableCities={availableCities}
        >
          <Layout
            showFooter={false}
            showMobileBottomNav={false}
            routeName={SEARCH_PAGE_ROUTE_NAME}
            noLoadingOnSamePath
          >
            <ListingsSearchPage />
          </Layout>
        </ListingsFilterProvider>
      </NuqsAdapter>
    </ThemeProvider>
  );
};

export default Page;
